import React, { useEffect, useState } from 'react';
import DeploymentLogEntry from '../components/DeploymentLogsTable/DeploymentLogEntry';
import { fetchDeploymentLogs } from '../services/api';
import MultiSelectDropdown from '../components/DeploymentLogsTable/MultiSelectDropdown';
import { DeploymentLog } from '../types/types';
import { normalizeTimestampDate } from '../utils/date';

const getSeason = (timestamp: number) => {
  const date = normalizeTimestampDate(timestamp);

  if (isNaN(date.getTime())) return 'Winter';

  const month = date.getMonth() + 1;
  const day = date.getDate();

  if (
    (month === 3 && day >= 21) ||
    (month > 3 && month < 6) ||
    (month === 6 && day <= 20)
  )
    return 'Spring';
  if (
    (month === 6 && day >= 21) ||
    (month > 6 && month < 9) ||
    (month === 9 && day <= 20)
  )
    return 'Summer';
  if (
    (month === 9 && day >= 21) ||
    (month > 9 && month < 12) ||
    (month === 12 && day <= 20)
  )
    return 'Fall';
  return 'Winter';
};

export default function DeploymentLogsTable() {
  const [allLogs, setAllLogs] = useState<DeploymentLog[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedSeasons, setSelectedSeasons] = useState<string[]>([]);
  const [selectedTrajectories, setSelectedTrajectories] = useState<string[]>(
    []
  );

  const [dynamicTags, setDynamicTags] = useState<string[]>([]);
  const [dynamicTrajectories, setDynamicTrajectories] = useState<string[]>([]);

  useEffect(() => {
    fetchDeploymentLogs()
      .then((logs) => {
        const normalizedLogs = logs.map((log: DeploymentLog) => {
          const timestampNum =
            typeof log.timestamp === 'string'
              ? parseInt(log.timestamp, 10)
              : log.timestamp;

          return {
            ...log,
            timestamp: timestampNum,
            tags: log.tags.map((t) => t.toLowerCase().trim()),
            trajectoryName: log.trajectoryName.toLowerCase().trim(),
            season: getSeason(timestampNum),
          };
        });

        setAllLogs(normalizedLogs);

        const tagsSet = new Set<string>();
        const trajSet = new Set<string>();
        normalizedLogs.forEach((log: DeploymentLog) => {
          log.tags.forEach((tag: string) => tagsSet.add(tag));
          trajSet.add(log.trajectoryName);
        });

        setDynamicTags(Array.from(tagsSet));
        setDynamicTrajectories(Array.from(trajSet));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filteredLogs = allLogs.filter((log) => {
    const tagMatch =
      selectedTags.length === 0 ||
      selectedTags.some((tag) => log.tags.includes(tag));

    const seasonMatch =
      selectedSeasons.length === 0 ||
      selectedSeasons.some(
        (season) => season.toLowerCase() === log.season.toLowerCase()
      );

    const trajectoryMatch =
      selectedTrajectories.length === 0 ||
      selectedTrajectories.some((traj) => traj === log.trajectoryName);

    return tagMatch && seasonMatch && trajectoryMatch;
  });

  if (loading)
    return (
      <p className="text-center text-gray-600 dark:text-gray-400">
        Loading logs...
      </p>
    );

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 text-gray-800 dark:text-gray-200">
      <h2 className="text-xl sm:text-2xl md:text-3xl text-center font-extrabold tracking-tight mb-6">
        Deployment LogBook
      </h2>

      <div className="mb-6 flex flex-wrap gap-4 justify-center">
        <MultiSelectDropdown
          label="Tags"
          options={dynamicTags}
          selected={selectedTags}
          onChange={(values) =>
            setSelectedTags(values.map((v) => v.toLowerCase().trim()))
          }
        />
        <MultiSelectDropdown
          label="Seasons"
          options={['Winter', 'Spring', 'Summer', 'Fall']}
          selected={selectedSeasons}
          onChange={setSelectedSeasons}
        />
        <MultiSelectDropdown
          label="Trajectories"
          options={dynamicTrajectories}
          selected={selectedTrajectories}
          onChange={(values) =>
            setSelectedTrajectories(values.map((v) => v.toLowerCase().trim()))
          }
        />
      </div>

      <div className="max-h-[70vh] overflow-y-auto space-y-6 pr-2 pt-6 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600">
        {filteredLogs.length === 0 ? (
          <p className="text-center text-sm text-gray-500">
            No logs match the selected filters.
          </p>
        ) : (
          filteredLogs.map((log, i) => <DeploymentLogEntry key={i} {...log} />)
        )}
      </div>
    </div>
  );
}
